import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed } from 'mobx';

import { permissionStore } from '@app/Stores/PermissionStore';
import { ClipboardService } from '@app/Services/ClipboardService';

import { appStore } from '@app/AppStore';

import { Icon } from '@app/Components/Icon';
import { modalService } from './Modal';
import { ToolTipItem } from './ToolTipItem';
import { AzureAuth, UserPermissions } from './AzureAuth';
import { NotificationHandler } from './ToastNotification';
import { applyDecorators, EmptyProps } from "@app/Helpers/Decorator";
const ImpersonationDialog = React.lazy(() => import('@app/Components/Dialogs/ImpersonationDialog'));

@observer
export class LoginSection extends React.Component {

    constructor (props:EmptyProps) {
        super(props);
        applyDecorators(this);
    }

    componentDidMount() {
        void permissionStore.loadRoles();
        window.addEventListener('storage', this._logOutEventHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('storage', this._logOutEventHandler);
    }

    render() {
        const id = `user_${appStore.currentUserName}`;
        const userName = appStore.impersonatedUser?.name ? appStore.impersonatedUser.name : appStore.currentUserName;
        const copyTitle = `Copy${appStore.impersonatedUser ? ' impersonated user' : ''} permissions`;
        return (
            <div className="user">
                <span id={id} className="user-name">
                    Hello, {userName}
                </span>
                <ToolTipItem
                    targetId={id}
                    className="user-tooltip"
                    placement="bottom"
                >
                    <div className="d-flex">
                        <div className="user-tooltip-text">{this._tooltipRolesStr}</div>
                        <Icon name="copy" title={copyTitle} className="user-tooltip-copy" onClick={this._copyToClipboard} />
                    </div>
                </ToolTipItem>

                {this._renderImpersonation()}
                <Icon name="sign-out-alt" title="Log out" onClick={() => AzureAuth.logOut(true)} />
            </div>
        );
    }

    @action.bound
    _logOutEventHandler(event: StorageEvent) {
        if (event.key === 'logout-event') {
            AzureAuth.logOut();
        }
    }

    @action.bound
    private _copyToClipboard() {
        const bytes = permissionStore.convertPermissionIdsToBytes(permissionStore.currentUserPermissions);
        const encodedString = permissionStore.encodeBytesToBase64(bytes);

        try {
            ClipboardService.copyToClipboard(encodedString);
            NotificationHandler.showSuccess(`Your permissions string ${encodedString} copied!`, 'Copy user permissions');
        } catch (err) {
            NotificationHandler.showError(`Oops, unable to copy: ${err}`);
        }
    }

    private _renderImpersonation() {
        if (!AzureAuth.hasAccess([UserPermissions.FullPermissionImpersonate, UserPermissions.LimitedPermissionImpersonate]) && !appStore.impersonatedUser) return null;

        if (appStore.impersonatedUser) {
            const impersonatedUserTooltipId = `impersonatedUser_tooltip`;
            const exitImpersonationTooltipId = `exitImpersonation_tooltip`;
            const exitImpersonateTooltip = `Exit from ${appStore.impersonatedUser.name ?? 'impersonated user'}`;
            return (
                <>
                    <Icon id={impersonatedUserTooltipId} name="user-secret" onClick={this._openImpersonationTool} />
                    <Icon id={exitImpersonationTooltipId} name="eye-slash" onClick={AzureAuth.exitImpersonation} />
                    <ToolTipItem targetId={impersonatedUserTooltipId} className="user-tooltip" text={this._tooltipRolesStr} placement="bottom" />
                    <ToolTipItem targetId={exitImpersonationTooltipId} className="user-tooltip" text={exitImpersonateTooltip} placement="bottom" />
                </>
            );
        }
        return <Icon name="hat-wizard" title="Impersonation" onClick={this._openImpersonationTool} />;
    }

    @action.bound
    private async _openImpersonationTool() {
        await modalService.show(ImpersonationDialog);
    }

    @computed
    private get _tooltipRolesStr() {
        const realRoles = permissionStore.getRolesMatchFromEncodedRoles(appStore.realUserRoles);
        const impersonatedRoles = permissionStore.getRolesMatchFromEncodedRoles(appStore.impersonatedUserRoles);

        const realUserName = appStore.impersonatedUser?.personId ? `Real name: ${appStore.currentUserName}.\n` : '';
        const realRolesString = realRoles ? `Real user: ${realRoles}.\n` : '';
        const impersonatedRolesString = appStore.impersonatedUser ? `Impersonated user: ${impersonatedRoles}.\n` : '';

        return `${realUserName}${realRolesString}${impersonatedRolesString}`;
    }
}